import { LowerCasePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { NgArrayPipesModule } from 'ngx-pipes'
import type { Observable } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  take,
} from 'rxjs'

import type { ProductDto } from '../../../api/responses'
import { OrganizationDropdownFilter } from '../../../components/filter-group/components/filters/organization-dropdown-filter'
import { InformationFieldComponent } from '../../../components/forms/information-field/information-field.component'
import { AuthService } from '../../../services/auth/auth.service'
import { OrganizationService } from '../../../services/organization/organization.service'
import { ProductsService } from '../../../services/product/products.service'
import { SubscriptionService } from '../../../services/subscription/subscription.service'
import { FeatureFlagDirective } from '../../../shared/directives/feature-flag.directive'
import { UserRole } from '../../../types/types'
import { ProductCardComponent } from '../components/product-card/product-card.component'

@Component({
  selector: 'dhp-pricing',
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FeatureFlagDirective,
    ProductCardComponent,
    InformationFieldComponent,
    TranslatePipe,
    NgArrayPipesModule,
    LowerCasePipe,
    OrganizationDropdownFilter,
  ],
})
export class ProductsComponent {
  private readonly productsService = inject(ProductsService)
  private readonly router = inject(Router)
  private readonly authService = inject(AuthService)
  private readonly organizationService = inject(OrganizationService)
  private readonly subscriptionService = inject(SubscriptionService)

  public readonly selectedProducts = new Map<number, number>()
  selectedOrganizationId = signal<number | null>(null)
  private readonly selectionVersion = signal(0)

  private readonly userMe = toSignal(this.authService.getUserMe())

  organization = computed(() => this.userMe()?.organization)
  hasOrganization = computed(() => !!this.userMe()?.organization)

  // The CMS only serves products to a billing admin with an organization.
  canSeeProducts = computed(
    () =>
      this.hasOrganization() &&
      !!this.authService.currentUser()?.roles?.includes(UserRole.BillingAdmin)
  )

  private readonly organizationItems = toSignal(
    this.organizationService.getOrganizationFilterItemsWithSelf(),
    {
      initialValue: new Set<
        import('../../../services/organization/organization.service').OrganizationFilterItem
      >(),
    }
  )

  // tri-state: null = unknown (items not loaded yet), true/false = resolved
  private readonly resellerStatus = computed<null | boolean>(() => {
    const items = this.organizationItems()
    // if still initial empty set, treat as unknown to prevent premature subscription fetch
    if (!items || items.size === 0) return null
    return items.size > 1
  })

  isReseller = computed(() => this.resellerStatus() === true)
  products = toSignal(
    toObservable(this.canSeeProducts).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.getProducts())
    )
  )

  /**
   * Subscription fetch rules:
   *
   * - Reseller: do NOT fetch until an organization is selected.
   * - Non-reseller: always fetch unfiltered (organizationId = undefined).
   */
  private readonly subscriptionOrgId = computed(() => {
    const reseller = this.resellerStatus()
    // Wait until we know whether user is reseller or not
    if (reseller === null) return 'SKIP' as const
    // Non-reseller: unfiltered call
    if (!reseller) return undefined
    // Reseller: only after selection
    return this.selectedOrganizationId() ?? null
  })

  // Create an observable driven by the signal so changes actually re-trigger
  private readonly subscriptionOrgId$ = toObservable(
    this.subscriptionOrgId
  ).pipe(distinctUntilChanged())

  subscribedProductIds = toSignal(
    this.subscriptionOrgId$.pipe(
      switchMap(orgId => {
        // still determining reseller state => do nothing
        if (orgId === 'SKIP') return of([] as number[])
        // reseller + no org selected => no call, return empties
        if (orgId === null) return of([] as number[])

        return this.subscriptionService
          .getSubscriptions(orgId ?? undefined)
          .pipe(
            map(subs => subs.map(sub => sub.product.id)),
            catchError((err: unknown) => {
              console.error(err)
              return of([] as number[])
            })
          )
      })
    ),
    { initialValue: [] }
  )

  constructor() {
    // When we switch between reseller/non-reseller or change org selection,
    // clear any previously selected products to avoid buying for wrong org.
    effect(() => {
      this.subscriptionOrgId()
      this.selectedProducts.clear()
      this.selectionVersion.update(version => version + 1)
    })

    effect(() => {
      if (!this.requiresMinimumSeats()) {
        return
      }

      if (this.normalizeMinimumSeatSelections()) {
        this.selectionVersion.update(version => version + 1)
      }
    })
  }

  availableProducts = computed(() =>
    this.products()?.filter(p => !this.isSubscribed(p.id))
  )

  readonly hasSubscribedContentIntegrator = computed(() =>
    (this.products() ?? []).some(
      product =>
        product.productType === 'content_integrator' &&
        this.isSubscribed(product.id)
    )
  )

  readonly hasSelectedContentIntegrator = computed(() => {
    this.selectionVersion()

    return (this.products() ?? []).some(
      product =>
        product.productType === 'content_integrator' &&
        (this.selectedProducts.get(product.id) ?? 0) > 0
    )
  })

  readonly requiresMinimumSeats = computed(
    () =>
      this.hasSubscribedContentIntegrator() ||
      this.hasSelectedContentIntegrator()
  )

  onSelectedUsersChange(product: ProductDto, users: number): void {
    this.selectedProducts.set(product.id, users)

    if (product.productType === 'content_integrator' && users > 0) {
      this.normalizeMinimumSeatSelections()
    }

    this.selectionVersion.update(version => version + 1)
  }

  async navigateToProductBasket(): Promise<void> {
    const selectedId = this.selectedOrganizationId()
    const organizationName =
      [...this.organizationItems()].find(item => item.id === selectedId)
        ?.name ?? null

    await this.router.navigate(['products', 'checkout'], {
      state: {
        products: Array.from(this.selectedProducts.entries()),
        organizationId: selectedId,
        organizationName,
      },
    })
  }

  hasSelectedProducts(): boolean {
    this.selectionVersion()

    return Array.from(this.selectedProducts.values()).some(value => value > 0)
  }

  getSelectedUsers(productId: number): number {
    this.selectionVersion()

    return this.selectedProducts.get(productId) ?? 0
  }

  getMinSelected(product: ProductDto): number | undefined {
    if (product.productType === 'content_integrator') {
      return 3
    }

    if (
      this.requiresMinimumSeats() &&
      this.isMinimumSeatRestrictedProduct(product.productType)
    ) {
      return 3
    }

    return undefined
  }

  isSubscribed(productId: number): boolean {
    return this.subscribedProductIds()?.includes(productId) ?? false
  }

  async createOrganization(): Promise<void> {
    await this.router.navigate(['/', 'organization', 'create'], {
      state: { returnTo: '/pricing' },
    })
  }

  private getProducts(): Observable<ProductDto[]> {
    return this.productsService.getProducts().pipe(
      catchError((err: unknown) => {
        console.error(err)
        return []
      })
    )
  }

  onSelectedOrganizationIdChange(organizationId: number | null): void {
    this.selectedOrganizationId.set(organizationId)
  }

  private isMinimumSeatRestrictedProduct(
    productType: ProductDto['productType']
  ): boolean {
    return productType === 'magazine' || productType === 'package'
  }

  private normalizeMinimumSeatSelections(): boolean {
    let hasChanges = false

    for (const product of this.products() ?? []) {
      if (!this.isMinimumSeatRestrictedProduct(product.productType)) {
        continue
      }

      const currentSeats = this.selectedProducts.get(product.id) ?? 0
      if (currentSeats > 0 && currentSeats < 3) {
        this.selectedProducts.set(product.id, 3)
        hasChanges = true
      }
    }

    return hasChanges
  }
}
